import type { Phrase } from './copy.js';

/**
 * The two board venues: the dama lattice and the Tongits bench.
 *
 * Board copy is terse and half of it is shouted across a table, so the English
 * is picked for length first — "Sapilitang kain" is "Must capture", not "A
 * capture is compulsory". Where the table term has a real English one it is
 * used: bunot is draw, tapon is discard, sapaw is top, tumba is knock, and the
 * crowned tansan is a king. The game titles themselves are proper nouns and are
 * not in here.
 */
export const DAMA = {
  /* ── Shared by both boards ────────────────────────────────────────────── */
  'dama.seat': { tl: 'Upuan {n}', en: 'Seat {n}' },

  /* ── Dama: the board ──────────────────────────────────────────────────── */
  'dama.wait.board': {
    tl: 'Naghihintay ng board mula sa server…',
    en: 'Waiting for a board from the server…',
  },
  'dama.board.label': { tl: 'Dama board', en: 'Dama board' },
  'dama.sq.mine': { tl: '{sq} — tansan mo', en: '{sq} — your cap' },
  'dama.sq.mine.dama': { tl: '{sq} — tansan mo, dama', en: '{sq} — your cap, king' },
  'dama.sq.theirs': { tl: '{sq} — tansan ng kalaban', en: '{sq} — opponent cap' },
  'dama.sq.theirs.dama': {
    tl: '{sq} — tansan ng kalaban, dama',
    en: '{sq} — opponent cap, king',
  },
  'dama.sq.seat': { tl: '{sq} — tansan ng Upuan {n}', en: '{sq} — Seat {n} cap' },
  'dama.sq.seat.dama': {
    tl: '{sq} — tansan ng Upuan {n}, dama',
    en: '{sq} — Seat {n} cap, king',
  },

  /* ── Dama: how it ended ───────────────────────────────────────────────── */
  'dama.over.tabla': { tl: 'Tabla.', en: 'Draw.' },
  'dama.over.seat': { tl: 'Panalo ang Upuan {n}.', en: 'Seat {n} wins.' },
  'dama.over.you': { tl: 'Panalo ka.', en: 'You win.' },
  'dama.over.lost': { tl: 'Talo ngayon.', en: 'Lost that one.' },
  'dama.why.todas': {
    tl: 'Nalinis ang buong board — todas.',
    en: 'The whole board is swept — wipeout.',
  },
  'dama.why.immobilised': { tl: 'Walang natirang legal na tira.', en: 'No legal move left.' },
  'dama.why.forfeit': { tl: 'May sumuko sa mesa.', en: 'Somebody quit the table.' },
  'dama.why.repetition': { tl: 'Paulit-ulit na ang posisyon.', en: 'The position keeps repeating.' },
  'dama.why.noprogress': { tl: 'Limampung tira nang walang kain.', en: 'Fifty moves, no capture.' },

  /* ── Dama: the toast line ─────────────────────────────────────────────── */
  'dama.tabla.head': { tl: 'Malapit nang tabla', en: 'Draw is close' },
  'dama.tabla.sub': {
    tl: '{n} tira na lang na walang kain at refund ang pusta.',
    en: '{n} more moves with no capture and the bets refund.',
  },
  'dama.force.head': { tl: 'Sapilitang kain', en: 'Must capture' },
  'dama.force.mine': {
    tl: 'ang pinakamaraming kunin lang ang legal.',
    en: 'only the biggest capture is legal.',
  },
  'dama.force.theirs': {
    tl: 'kailangang kumain ng nasa tira.',
    en: 'the seat on the clock has to capture.',
  },
  'dama.turn.over': { tl: 'Tapos na ang laro.', en: 'The game is over.' },
  'dama.turn.seat': { tl: 'Tira ng Upuan {n}.', en: 'Seat {n} to move.' },
  'dama.turn.you': { tl: 'Ikaw ang tira.', en: 'Your move.' },
  'dama.turn.them': { tl: 'Kalaban ang tira.', en: 'Opponent to move.' },

  /* ── Dama: pills and trays ────────────────────────────────────────────── */
  'dama.act.play': { tl: 'Tira na', en: 'Play it' },
  'dama.act.undo': { tl: 'Bawiin', en: 'Undo' },
  'dama.tray.mine': { tl: 'Tansan mo', en: 'Your caps' },
  'dama.tray.theirs': { tl: 'Kalaban', en: 'Opponent' },
  'dama.tray.crowned': { tl: '{n} dama', en: '{n} crowned' },

  /* ── Tongits: the table ───────────────────────────────────────────────── */
  'dama.tng.wait': {
    tl: 'Naghihintay ng mesa mula sa server…',
    en: 'Waiting for a table from the server…',
  },
  'dama.tng.stock': { tl: 'Bunutan, {n} baraha', en: 'Draw pile, {n} cards' },
  'dama.tng.tapon.empty': { tl: 'Walang tapon', en: 'No discard' },
  'dama.tng.tapon.take': { tl: 'Kunin ang tapon, {card}', en: 'Take the discard, {card}' },
  'dama.tng.cards': { tl: '{n} baraha', en: '{n} cards' },

  /* ── Tongits: the cards ───────────────────────────────────────────────── */
  'dama.tng.card': { tl: '{rank} ng {suit}', en: '{rank} of {suit}' },
  'dama.tng.card.owed': { tl: '{card}, kinuha sa tapon', en: '{card}, taken from the discard' },
  'dama.tng.suit.S': { tl: 'espada', en: 'spades' },
  'dama.tng.suit.H': { tl: 'puso', en: 'hearts' },
  'dama.tng.suit.D': { tl: 'diamante', en: 'diamonds' },
  'dama.tng.suit.C': { tl: 'trebol', en: 'clubs' },

  /* ── Tongits: melds on the plywood ────────────────────────────────────── */
  'dama.tng.cue.sapaw': { tl: 'sapaw', en: 'top' },
  'dama.tng.kind.set': { tl: 'Set', en: 'Set' },
  'dama.tng.kind.run': { tl: 'Run', en: 'Run' },
  'dama.tng.meld.mine': { tl: '{kind} mo', en: 'Your {kind}' },
  'dama.tng.meld.mine.top': { tl: '{kind} mo, pwedeng sapawan', en: 'Your {kind}, can be topped' },
  'dama.tng.meld.seat': { tl: '{kind} ni Upuan {n}', en: "Seat {n}'s {kind}" },
  'dama.tng.meld.seat.top': {
    tl: '{kind} ni Upuan {n}, pwedeng sapawan',
    en: "Seat {n}'s {kind}, can be topped",
  },

  /* ── Tongits: the rail ────────────────────────────────────────────────── */
  'dama.tng.rail.watch': {
    tl: 'Nanonood ka — walang kamay sa rail.',
    en: 'You are watching — no hand on the rail.',
  },
  'dama.tng.rail.empty': { tl: 'Wala ka nang hawak.', en: 'You are out of cards.' },

  /* ── Tongits: the toast line ──────────────────────────────────────────── */
  'dama.tng.fight.head': { tl: 'Tumumba si {who}', en: '{who} knocked' },
  'dama.tng.fight.ask': {
    tl: 'laban ka o atras? Kung atras, bilang pa rin ang puntos mo pero wala kang panalo.',
    en: 'fight or fold? Fold and your points still count, but you cannot win.',
  },
  'dama.tng.fight.wait': { tl: 'hinihintay ang sagot ng iba.', en: 'waiting on the others.' },
  'dama.tng.tumba.warn': {
    tl: 'kung hindi ikaw ang pinakamababa, sunog ka.',
    en: 'if you are not the lowest, you burn.',
  },
  'dama.tng.turn.over': { tl: 'Tapos na ang hand.', en: 'The hand is over.' },
  'dama.tng.turn.wait': { tl: 'Naghihintay…', en: 'Waiting…' },
  'dama.tng.turn.seat': { tl: 'Tira ng Upuan {n}.', en: 'Seat {n} to play.' },
  'dama.tng.turn.draw': { tl: 'Ikaw — bumunot ka.', en: 'You — draw.' },
  'dama.tng.turn.owed': { tl: 'Ikaw — ibaba ang tapon.', en: 'You — meld the discard.' },
  'dama.tng.turn.act': { tl: 'Ikaw — ibaba o tapon.', en: 'You — meld or discard.' },

  /* ── Tongits: pills and counters ──────────────────────────────────────── */
  'dama.tng.act.laban': { tl: 'Laban', en: 'Fight' },
  'dama.tng.act.atras': { tl: 'Atras', en: 'Fold' },
  'dama.tng.act.tumba': { tl: 'Tumba', en: 'Knock' },
  'dama.tng.act.tumba.sure': { tl: 'Sigurado — tumumba', en: 'Sure — knock' },
  'dama.tng.act.back': { tl: 'Bawi', en: 'Back' },
  'dama.tng.act.meld': { tl: 'Ibaba', en: 'Meld' },
  'dama.tng.act.discard': { tl: 'Tapon', en: 'Discard' },
  'dama.tng.act.clear': { tl: 'Bawiin', en: 'Clear' },
  'dama.tng.count.stock': { tl: 'Bunutan', en: 'Draw' },
  'dama.tng.count.discard': { tl: 'Tapon', en: 'Discard' },
  'dama.tng.count.points': { tl: 'Puntos mo', en: 'Your points' },

  /* ── Tongits: the bilangan ────────────────────────────────────────────── */
  'dama.tng.someone': { tl: 'Isa sa mesa', en: 'Someone at the table' },
  'dama.tng.you': { tl: 'Ikaw', en: 'You' },
  'dama.tng.end.laban': { tl: 'Laban!', en: 'Fight!' },
  'dama.tng.end.exhausted': { tl: 'Ubos na ang bunutan', en: 'The draw pile is out' },
  'dama.tng.end.called': { tl: 'Tinawag ni {who}. Bilangan na.', en: '{who} called it. Count up.' },
  'dama.tng.end.nodraw': {
    tl: 'Walang makakakuha ng tapon — bilangan na.',
    en: 'Nobody can take the discard — count up.',
  },
  'dama.tng.end.won': { tl: 'panalo', en: 'won' },
  'dama.tng.end.burned': { tl: 'sunog', en: 'burned' },
  'dama.tng.end.tabla': { tl: 'Tabla — walang kumuha ng pot.', en: 'Draw — nobody takes the pot.' },
  'dama.tng.end.tongits': {
    tl: 'Naubos ni {who} ang kamay. Walang bilangan.',
    en: '{who} emptied their hand. No count-up.',
  },
  /* Singulars: English inflects after a count and Taglish does not. */
  'dama.tng.stock.one': { tl: 'Bunutan, {n} baraha', en: 'Draw pile, {n} card' },
  'dama.tng.cards.one': { tl: '{n} baraha', en: '{n} card' },
} as const satisfies Record<string, Phrase>;
